import type { AxiosInstance } from 'axios';

import type { PricingSignal } from './intelligence';

export type RecommendationSurface =
  | 'SIMILAR'
  | 'CROSS_SELL'
  | 'UPSELL'
  | 'PERSONALIZED'
  | 'CART'
  | 'RECENTLY_VIEWED'
  | 'B2B_REORDER';

export interface RecommendationItem {
  productId: string;
  scoreBps: number;
  reason: string;
}

export interface RecommendationResult {
  surface: RecommendationSurface;
  seedId: string | null;
  items: RecommendationItem[];
}

export interface PricingScanResult {
  scanned: number;
  proposed: PricingSignal[];
  durationMs: number;
}

export interface FraudScanResult {
  scanned: number;
  flagged: number;
  detectorsRun: string[];
  durationMs: number;
}

export interface ContentTag {
  productId: string;
  tags: string[];
  seo: { title: string; description: string };
}

export interface AiJob {
  id: string;
  kind: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'ROLLED_BACK';
  progress: number;
  totalItems: number;
  result: any | null;
  createdAt: string;
  updatedAt: string;
}

export interface AiJobStartResult {
  jobId: string;
}

export function aiEndpoints(http: AxiosInstance) {
  return {
    similar: (productId: string, limit = 10) =>
      http.get<RecommendationResult>(`/ai/recommendations/similar/${productId}`, { params: { limit } }).then((r) => r.data),
    crossSell: (productId: string, limit = 10) =>
      http.get<RecommendationResult>(`/ai/recommendations/cross-sell/${productId}`, { params: { limit } }).then((r) => r.data),
    upsell: (productId: string, limit = 10) =>
      http.get<RecommendationResult>(`/ai/recommendations/upsell/${productId}`, { params: { limit } }).then((r) => r.data),
    personalized: (limit = 10) =>
      http.get<RecommendationResult>('/ai/recommendations/personalized', { params: { limit } }).then((r) => r.data),
    cart: (cartId: string, limit = 10) =>
      http.get<RecommendationResult>(`/ai/recommendations/cart/${cartId}`, { params: { limit } }).then((r) => r.data),
    b2bReorder: (accountId: string, limit = 10) =>
      http.get<RecommendationResult>(`/ai/recommendations/b2b/${accountId}/reorder`, { params: { limit } }).then((r) => r.data),

    // Admin
    personalizedForUser: (userId: string, limit = 10) =>
      http.get<RecommendationResult>(`/admin/ai/recommendations/users/${userId}`, { params: { limit } }).then((r) => r.data),
    runPricingScan: (body: { limit?: number; detectors?: string[]; validityHours?: number }) =>
      http.post<AiJobStartResult>('/admin/ai/pricing/scan', body).then((r) => r.data),
    runFraudScan: (body: { limit?: number; detectors?: string[] }) =>
      http.post<AiJobStartResult>('/admin/ai/fraud/scan', body).then((r) => r.data),
    tagProduct: (body: { productId: string; persist?: boolean }) =>
      http.post<AiJobStartResult>('/admin/ai/content/tag-product', body).then((r) => r.data),

    // Jobs
    listJobs: () => http.get<AiJob[]>('/admin/ai/jobs').then((r) => r.data),
    getJob: (jobId: string) => http.get<AiJob>(`/admin/ai/jobs/${jobId}`).then((r) => r.data),
    rollbackJob: (jobId: string) => http.post<void>(`/admin/ai/jobs/${jobId}/rollback`).then((r) => r.data),
  };
}

export type AiEndpoints = ReturnType<typeof aiEndpoints>;
